import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useFormikContext } from 'formik'
import { Button, ButtonGroup, Grid, H, P, Wrapper } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'

import DatetimePicker from 'components/form/datetime-picker'
import Input from 'components/form/input'
import { SelectInput } from 'components/form/select-input'
import InputHint from 'components/styled/input-hint'

import { ACTIVE_BRANDS, BrandId } from 'config/brands'
import { VoucherDiscount, VoucherMechanism } from 'lib/models/voucher'
import { getGiftCardOfferMessage, getVoucherProductsForBrand } from '../helpers'
import { type VoucherFormValues } from '../interfaces'

const DISCOUNT_TYPE_OPTIONS = [
  { label: 'Percentage', value: VoucherDiscount.percentage },
  { label: 'Value (pence)', value: VoucherDiscount.valueInPence },
  { label: 'Gift card', value: VoucherDiscount.giftCard },
]

const MECHANISM_OPTIONS = [
  { label: 'None', value: '' },
  { label: 'Membership', value: VoucherMechanism.membership },
  { label: 'Member get member', value: VoucherMechanism.memberGetMember },
]

interface VoucherFormProps {
  isUsed: boolean
  backPath: string
  isSubmitting?: boolean
}

const VoucherForm = ({
  isUsed,
  backPath,
  isSubmitting,
}: VoucherFormProps): React.ReactElement => {
  const { values, setFieldValue } = useFormikContext<VoucherFormValues>()
  const isGiftCard = values.discountType === VoucherDiscount.giftCard
  const readOnly = isUsed
  const giftCardOfferMessage = getGiftCardOfferMessage({
    partnerName: values.offerAttributes.partnerName,
    offer: values.offerAttributes.offer,
    productName: values.productName,
  })

  const productOptions = useMemo(
    () => getVoucherProductsForBrand(values.brandId as BrandId),
    [values.brandId]
  )

  /** Clear the selected product if the chosen brand does not offer it (e.g. when
   * switching brand after a product was already selected). */
  useEffect(() => {
    if (
      values.productName &&
      !productOptions.some((option) => option.value === values.productName)
    ) {
      setFieldValue('productName', '')
    }
  }, [productOptions, values.productName, setFieldValue])

  return (
    <Grid padding={[GTR.L, 0, 0]}>
      <Grid.Item spanFromL={7}>
        {isUsed && (
          <Wrapper
            margin={[0, 0, GTR.L]}
            padding={GTR.M}
            style={{
              background: '#fff3cd',
              borderRadius: 4,
              border: '1px solid #ffc107',
            }}
          >
            <P>
              This voucher has been used. Only the <strong>expiry date</strong>
              {isGiftCard && (
                <>
                  {' '}
                  and <strong>offer description</strong>
                </>
              )}{' '}
              can be changed.
            </P>
          </Wrapper>
        )}

        <H size="S" margin={[0, 0, GTR.S]}>
          Voucher details
        </H>

        <Grid gap="S" margin={[0, 0, GTR.M]}>
          <Grid.Item spanFromM={6}>
            <SelectInput
              label="Brand"
              name="brandId"
              options={ACTIVE_BRANDS}
              disabled={readOnly}
            />
          </Grid.Item>
          <Grid.Item spanFromM={6}>
            <SelectInput
              label="Product"
              name="productName"
              options={productOptions}
              disabled={readOnly || !values.brandId}
              hint={
                values.brandId
                  ? undefined
                  : 'Select a brand first to see its products.'
              }
            />
          </Grid.Item>
          <Grid.Item spanFromM={6}>
            <Input
              label="Code"
              name="code"
              disabled={readOnly}
              hint="Lowercase alphanumeric. Auto-lowercased on save."
            />
          </Grid.Item>
        </Grid>

        <H size="S" margin={[GTR.M, 0, GTR.S]}>
          Offer
        </H>

        <Grid gap="S" margin={[0, 0, GTR.M]}>
          <Grid.Item spanFromM={6}>
            <SelectInput
              label="Voucher type"
              name="discountType"
              options={DISCOUNT_TYPE_OPTIONS}
              disabled={readOnly}
              hint="percentage = % off, value_in_pence = fixed amount off, gift_card = a partner gift card (no monetary discount)."
            />
          </Grid.Item>
          {!isGiftCard && (
            <Grid.Item spanFromM={6}>
              <Input
                label={
                  values.discountType === VoucherDiscount.percentage
                    ? 'Discount value (%)'
                    : 'Discount value (pence)'
                }
                name="discountValue"
                type="number"
                disabled={readOnly}
                hint={
                  values.discountType === VoucherDiscount.percentage
                    ? 'e.g. 50 for 50% off'
                    : 'e.g. 5000 for £50 off'
                }
              />
            </Grid.Item>
          )}
        </Grid>

        <H size="S" margin={[GTR.M, 0, GTR.S]}>
          Constraints
        </H>

        <Grid gap="S" margin={[0, 0, GTR.M]}>
          <Grid.Item spanFromM={6}>
            <DatetimePicker
              label="Expiry date"
              name="expiresAt"
              hint="Vouchers require an expiry date. Defaults to the 1st of next month."
            />
          </Grid.Item>
          <Grid.Item spanFromM={6}>
            <Input
              label="Usage limit (optional)"
              name="usageLimit"
              type="number"
              hint="Leave blank for unlimited usage. If set after first use, must be greater than current usage."
            />
          </Grid.Item>
          <Grid.Item spanFromM={6}>
            <SelectInput
              label="Mechanism (optional)"
              name="mechanism"
              options={MECHANISM_OPTIONS}
              isClearable
              disabled={readOnly}
              hint="membership = only valid for members; member_get_member = referral voucher."
            />
          </Grid.Item>
        </Grid>

        {isGiftCard && (
          <>
            <H size="S" margin={[GTR.M, 0, GTR.S]}>
              Gift card details
            </H>
            <Grid gap="S" margin={[0, 0, GTR.M]}>
              <Grid.Item>
                <Input
                  label="Partner name"
                  name="offerAttributes.partnerName"
                  disabled={readOnly}
                  hint="The name of the gift card partner (e.g. Restless)."
                />
              </Grid.Item>
              <Grid.Item>
                <Input
                  label="Offer description"
                  name="offerAttributes.offer"
                  component="textarea"
                  disabled={readOnly && !isGiftCard}
                  hint="A short description shown to the customer (e.g. a free £250 gift card)."
                />
              </Grid.Item>
              <Grid.Item>
                <P size="M" bold margin={[0, 0, GTR.XXS]}>
                  Message displayed
                </P>
                <InputHint>{giftCardOfferMessage}</InputHint>
              </Grid.Item>
            </Grid>
          </>
        )}

        <ButtonGroup horizontalFromS margin={[GTR.L, 0, 0]}>
          <Button.Primary type="submit" loading={isSubmitting}>
            Save voucher
          </Button.Primary>
          <Button.Plain tag={Link} back to={backPath}>
            Cancel
          </Button.Plain>
        </ButtonGroup>
      </Grid.Item>
    </Grid>
  )
}

export default VoucherForm
